import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import StatusBadge from './StatusBadge';
import PriorityBadge from './PriorityBadge';
import { formatDate, isOverdue } from '../utils/dateHelpers';

const QuickTaskPanel = ({ panel, onClose, onOpenTask, users = [] }) => {
  const { open, title, tasks, loading, accentColor } = panel;

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/30 z-40 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 280 }}
            className="fixed top-0 right-0 h-full w-[440px] z-50 flex flex-col shadow-2xl"
            style={{ background: 'hsl(var(--background))' }}
          >
            {/* Header */}
            <div
              className="px-5 py-4 flex items-center justify-between border-b border-border"
              style={{ borderTop: `4px solid ${accentColor}` }}
            >
              <div>
                <h2 className="font-bold text-foreground text-base">{title}</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {loading ? 'Cargando...' : `${tasks.length} tarea${tasks.length !== 1 ? 's' : ''}`}
                </p>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Task list */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center h-40">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : tasks.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                  <p className="text-3xl mb-2">✅</p>
                  <p className="text-sm font-medium">Sin tareas en esta categoría</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {tasks.map((task, idx) => {
                    const responsible = users.find(u => u.id === task.responsible_user_id);
                    const overdue = isOverdue(task.due_date, task.status);

                    return (
                      <motion.button
                        key={task.id}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.03 }}
                        onClick={() => onOpenTask(task.id)}
                        className="w-full text-left px-5 py-4 hover:bg-secondary/50 transition-colors group"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                              {task.title}
                            </p>
                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                              <StatusBadge status={task.status} waitingType={task.waiting_type} />
                              <PriorityBadge priority={task.priority} />
                            </div>
                          </div>
                          <svg className="w-4 h-4 text-muted-foreground group-hover:text-primary flex-shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-all" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>

                        <div className="flex items-center gap-3 mt-2">
                          {responsible && (
                            <div className="flex items-center gap-1.5">
                              <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-[10px]">
                                {responsible.name.charAt(0).toUpperCase()}
                              </div>
                              <span className="text-xs text-muted-foreground">{responsible.name}</span>
                            </div>
                          )}
                          {task.due_date && (
                            <span className={`text-xs font-medium ${overdue ? 'text-destructive' : 'text-muted-foreground'}`}>
                              📅 {formatDate(task.due_date)}
                            </span>
                          )}
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-border bg-secondary/20">
              <p className="text-xs text-center text-muted-foreground">
                Haz click en una tarea para ver el detalle y editarla
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default QuickTaskPanel;
